import type { InjectionKey } from "vue"
import { computed, inject, provide, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useToast } from "primevue/usetoast"
import { useI18n } from "vue-i18n"
import { METER_TYPES_LIST } from "@core/constants"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useTaskStore } from "@core/stores/task.store"
import { createUniqueId } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants/source"
import type { SourceTypeChoice } from "@core/components/source/form/append/machine/source-append.machine"
import type { Services } from "@services"
import type {
    PvFormPayload,
    PvSegment,
} from "@modules/pv/components/dimensionnement/form/pv-form.types"
import type {
    DimensionnementPvAnalysis,
    PvSegmentParam,
} from "@modules/pv/types/dimensionnement-pv.types"
import { usePvStore } from "../stores/pv.store"

/**
 * API partagée entre {@link PageBuildingPVWrapper} (provide) et les sous-pages PV (inject).
 */
export type PvPageShellApi = ReturnType<typeof createPvPageShell>

export const PV_PAGE_SHELL_KEY: InjectionKey<PvPageShellApi> =
    Symbol("pvPageShell")

/**
 * Instancie le shell PV (dialogues, toasts d’étude éco, rafraîchissement après ajout PDL).
 * Préférer {@link providePvPageShell} à la racine des routes PV bâtiment.
 */
export function createPvPageShell() {
    const route = useRoute()
    const router = useRouter()
    const toast = useToast()
    const buildingStore = useBuildingStore()
    const pvStore = usePvStore()
    const taskStore = useTaskStore()
    const services = inject<Services>("services")
    if (!services) {
        throw new Error("createPvPageShell: services non injectés")
    }
    const { $createAnalysePv, $updateAnalysePv } = services

    // useScope: "global" : une instance de composant ne peut avoir qu'un seul composer i18n « local ».
    // Les pages PV peuvent déjà appeler useI18n local avant ce composable ; sans global, ce bloc
    // est ignoré et les clés toast* restent introuvables (affichage de la clé brute).
    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                toastCreateError:
                    "La création de l’étude PV a échoué. Vérifiez les paramètres puis réessayez.",
                toastUpdateError:
                    "La modification de l’étude PV a échoué. Vérifiez les paramètres puis réessayez.",
                toastCreateSuccess:
                    "La simulation de l’étude PV a été lancée avec succès.",
                toastUpdateSuccess:
                    "La mise à jour de l’étude PV a été lancée.",
                toastEcoCreated:
                    "L’étude économique a été lancée. Les résultats seront disponibles une fois le calcul terminé.",
                toastEcoRefreshError:
                    "L’étude a été créée mais le rafraîchissement des données a échoué.",
            },
        },
    })

    const getBuildingId = computed((): string | undefined => {
        const id = route.params._id_building
        return typeof id === "string" && id.length > 0 ? id : undefined
    })

    const sourceAppendDialogVisible = ref(false)
    const sourceAppendFromState = ref<SourceTypeChoice | undefined>(undefined)

    const dimensionnementDialogVisible = ref(false)
    const currentDimensionnementAnalyse = ref<DimensionnementPvAnalysis | null>(
        null
    )
    const isSubmittingDimensionnement = ref(false)

    const etudeEcoDialogVisible = ref(false)
    const etudeEcoDimensionnementId = ref<string | null>(null)

    const sourceAppendDialogKey = computed(
        () =>
            `pv-source-append-${getBuildingId.value}-${String(sourceAppendFromState.value ?? "all")}-${sourceAppendDialogVisible.value}`
    )

    watch(etudeEcoDialogVisible, (open) => {
        if (!open) {
            etudeEcoDimensionnementId.value = null
        }
    })

    watch(dimensionnementDialogVisible, (open) => {
        if (!open) {
            currentDimensionnementAnalyse.value = null
        }
    })

    const getCurrentSegment = computed(() => {
        const sid = pvStore.getSourceId
        if (!sid) return "C5"
        const pdl = buildingStore.getMeters.find(
            (meter) => meter._id_source === sid
        )
        const segment = pdl?.informations?.pdl?.compteur?.segment
        return typeof segment === "string" && segment.length > 0
            ? segment
            : "C5"
    })
    const getSourceId = computed(() => pvStore.getSourceId)
    const getExtrapolationId = computed(() => pvStore.getExtrapolationId)

    const getCurrentDimensionnementForm = computed<PvFormPayload | null>(() => {
        const analyse = currentDimensionnementAnalyse.value
        if (!analyse) return null
        const inputs = analyse.inputs
        return {
            name: analyse.name_analyse ?? "",
            extrapolation: Boolean(inputs?.extrapolation),
            _id_extrapolation: inputs?._id_extrapolation ?? null,
            listParamsPv: (inputs?.list_params_pv ?? []).map(
                (segment, index) => ({
                    id: segment.id || `segment-${index + 1}`,
                    peakpower: segment.peakpower ?? null,
                    pvtechchoice: segment.pvtechchoice || "crystSi",
                    loss: segment.loss ?? null,
                    optimalangles: segment.optimalangles ?? 1,
                    angle: segment.angle ?? 0,
                    aspect: segment.aspect ?? 0,
                    area: segment.area ?? null,
                    location:
                        Array.isArray(segment.location) &&
                        segment.location.length === 2
                            ? (segment.location as [number, number])
                            : null,
                    selected: true,
                    index: segment.index ?? index,
                })
            ),
        }
    })

    const getCurrentDimensionnementAnalyseId = computed(
        () => currentDimensionnementAnalyse.value?._id ?? null
    )

    /**
     * Convertit un segment issu de l’API en entrée formulaire / PUT dimensionnement.
     */
    function mapSegmentParamToFormSegment(segment: PvSegmentParam): PvSegment {
        const loc = segment.location
        return {
            id: segment.id,
            peakpower: segment.peakpower,
            pvtechchoice: segment.pvtechchoice,
            loss: segment.loss,
            optimalangles: segment.optimalangles,
            angle: segment.angle,
            aspect: segment.aspect,
            area: segment.area,
            location:
                Array.isArray(loc) &&
                loc.length === 2 &&
                typeof loc[0] === "number" &&
                typeof loc[1] === "number"
                    ? [loc[0], loc[1]]
                    : null,
            selected: true,
            index: segment.index,
        }
    }

    /**
     * Corps attendu par POST/PUT `/api/metier/v5/pv/dimensionnement` (champs snake_case, pas de wrapper `dataAnalyse`).
     */
    function buildDimensionnementApiBody(
        formPayload: PvFormPayload,
        buildingId: string,
        sourceId: string
    ): Record<string, unknown> {
        const list_params_pv = formPayload.listParamsPv
            .filter((s) => s.selected !== false)
            .map((s) => ({
                id: s.id,
                index: s.index,
                peakpower: s.peakpower ?? 0,
                pvtechchoice: s.pvtechchoice || "crystSi",
                loss: s.loss ?? 0,
                optimalangles: s.optimalangles,
                angle: s.angle,
                aspect: s.aspect,
                area: s.area,
                location:
                    Array.isArray(s.location) &&
                    s.location.length === 2 &&
                    typeof s.location[0] === "number" &&
                    typeof s.location[1] === "number"
                        ? [s.location[0], s.location[1]]
                        : null,
            }))

        const body: Record<string, unknown> = {
            _id_building: buildingId,
            _id_source: sourceId,
            name_analyse: formPayload.name,
            extrapolation: formPayload.extrapolation,
            list_params_pv,
        }

        if (formPayload.extrapolation && formPayload._id_extrapolation) {
            body._id_extrapolation = formPayload._id_extrapolation
        }

        return body
    }

    /**
     * Relance l’analyse avec un sous-ensemble de pans (sans ouvrir la modale d’édition complète).
     */
    async function onRelaunchDimensionnementWithSegments(
        analyse: DimensionnementPvAnalysis,
        selectedSegments: PvSegmentParam[]
    ): Promise<void> {
        const bid = getBuildingId.value
        const sid = pvStore.getSourceId
        const analyseId = analyse._id
        if (!bid || !sid || !analyseId || selectedSegments.length === 0) return

        const formPayload: PvFormPayload = {
            name: analyse.name_analyse ?? "",
            extrapolation: Boolean(analyse.inputs?.extrapolation),
            _id_extrapolation: analyse.inputs?._id_extrapolation ?? null,
            listParamsPv: selectedSegments.map(mapSegmentParamToFormSegment),
        }

        isSubmittingDimensionnement.value = true
        try {
            const updated = await $updateAnalysePv(
                analyseId,
                buildDimensionnementApiBody(formPayload, bid, sid),
                { event_name: createUniqueId() }
            )
            const taskData = updated as {
                task_id?: string
                event_name?: string
            }

            if (taskData.task_id && taskData.event_name) {
                taskStore.followEvent(taskData.task_id, taskData.event_name, {
                    taskname: "analyse.pv.dimensionnement.update",
                    taskToastTitle: "Modification de l'étude PV",
                    notification: true,
                    autoDelete: true,
                    onSuccess: () => {
                        void refreshPvDataAfterDimensionnement()
                    },
                    onError: () => {
                        toast.add({
                            severity: "error",
                            summary: t("toastUpdateError"),
                            life: 6000,
                        })
                    },
                })
            } else if (taskData.task_id) {
                taskStore.followTask(taskData.task_id, {
                    taskname: "analyse.pv.dimensionnement.update",
                    notification: true,
                    autoDelete: true,
                    onSuccess: () => {
                        void refreshPvDataAfterDimensionnement()
                    },
                    onError: () => {
                        toast.add({
                            severity: "error",
                            summary: t("toastUpdateError"),
                            life: 6000,
                        })
                    },
                })
            } else {
                await refreshPvDataAfterDimensionnement()
            }
            toast.add({
                severity: "success",
                summary: t("toastUpdateSuccess"),
                life: 5000,
            })
        } catch {
            toast.add({
                severity: "error",
                summary: t("toastUpdateError"),
                life: 6000,
            })
        } finally {
            isSubmittingDimensionnement.value = false
        }
    }

    /**
     * Rafraîchit les données nécessaires après création / modification d’un dimensionnement.
     */
    async function refreshPvDataAfterDimensionnement(): Promise<void> {
        const bid = getBuildingId.value
        const sid = pvStore.getSourceId
        if (!bid || !sid) return
        const ids = { sourceId: sid, buildingId: bid }
        await Promise.all([
            pvStore.fetchLastEtudePvBySource(ids),
            pvStore.fetchDimensionnementHistoryBySource(ids),
            pvStore.fetchLastEtudeEcoForSource(ids),
            pvStore.fetchValorisationHistoryBySource(ids),
        ])
    }

    function onClickCreateDimensionnement(): void {
        currentDimensionnementAnalyse.value = null
        dimensionnementDialogVisible.value = true
    }

    function onClickEditDimensionnement(
        analyse: DimensionnementPvAnalysis
    ): void {
        currentDimensionnementAnalyse.value = analyse
        dimensionnementDialogVisible.value = true
    }

    function onCloseDimensionnementDialog(): void {
        dimensionnementDialogVisible.value = false
        currentDimensionnementAnalyse.value = null
    }

    async function onCreateDimensionnement(
        formPayload: PvFormPayload
    ): Promise<void> {
        const bid = getBuildingId.value
        const sid = pvStore.getSourceId
        if (!bid || !sid) return

        isSubmittingDimensionnement.value = true
        try {
            const created = await $createAnalysePv(
                buildDimensionnementApiBody(formPayload, bid, sid),
                { event_name: createUniqueId() }
            )
            const taskData = created as {
                task_id?: string
                event_name?: string
            }

            onCloseDimensionnementDialog()
            if (taskData.task_id && taskData.event_name) {
                taskStore.followEvent(taskData.task_id, taskData.event_name, {
                    taskname: "analyse.pv.dimensionnement",
                    taskToastTitle: "Simulation du scénario de production",
                    notification: true,
                    autoDelete: true,
                    onSuccess: () => {
                        void refreshPvDataAfterDimensionnement()
                    },
                    onError: () => {
                        toast.add({
                            severity: "error",
                            summary: t("toastCreateError"),
                            life: 6000,
                        })
                    },
                })
            } else if (taskData.task_id) {
                taskStore.followTask(taskData.task_id, {
                    taskname: "analyse.pv.dimensionnement",
                    notification: true,
                    autoDelete: true,
                    onSuccess: () => {
                        void refreshPvDataAfterDimensionnement()
                    },
                    onError: () => {
                        toast.add({
                            severity: "error",
                            summary: t("toastCreateError"),
                            life: 6000,
                        })
                    },
                })
            } else {
                await refreshPvDataAfterDimensionnement()
            }
            toast.add({
                severity: "success",
                summary: t("toastCreateSuccess"),
                life: 5000,
            })
        } catch {
            toast.add({
                severity: "error",
                summary: t("toastCreateError"),
                life: 6000,
            })
        } finally {
            isSubmittingDimensionnement.value = false
        }
    }

    async function onUpdateDimensionnement(
        formPayload: PvFormPayload
    ): Promise<void> {
        const bid = getBuildingId.value
        const sid = pvStore.getSourceId
        const analyseId = currentDimensionnementAnalyse.value?._id
        if (!bid || !sid || !analyseId) return

        isSubmittingDimensionnement.value = true
        try {
            const updated = await $updateAnalysePv(
                analyseId,
                buildDimensionnementApiBody(formPayload, bid, sid),
                { event_name: createUniqueId() }
            )
            const taskData = updated as {
                task_id?: string
                event_name?: string
            }

            onCloseDimensionnementDialog()
            if (taskData.task_id && taskData.event_name) {
                taskStore.followEvent(taskData.task_id, taskData.event_name, {
                    taskname: "analyse.pv.dimensionnement.update",
                    taskToastTitle: "Modification de l'étude PV",
                    notification: true,
                    autoDelete: true,
                    onSuccess: () => {
                        void refreshPvDataAfterDimensionnement()
                    },
                    onError: () => {
                        toast.add({
                            severity: "error",
                            summary: t("toastUpdateError"),
                            life: 6000,
                        })
                    },
                })
            } else if (taskData.task_id) {
                taskStore.followTask(taskData.task_id, {
                    taskname: "analyse.pv.dimensionnement.update",
                    notification: true,
                    autoDelete: true,
                    onSuccess: () => {
                        void refreshPvDataAfterDimensionnement()
                    },
                    onError: () => {
                        toast.add({
                            severity: "error",
                            summary: t("toastUpdateError"),
                            life: 6000,
                        })
                    },
                })
            } else {
                await refreshPvDataAfterDimensionnement()
            }
            toast.add({
                severity: "success",
                summary: t("toastUpdateSuccess"),
                life: 5000,
            })
        } catch {
            toast.add({
                severity: "error",
                summary: t("toastUpdateError"),
                life: 6000,
            })
        } finally {
            isSubmittingDimensionnement.value = false
        }
    }

    function onClickModalEtudeEco(analyseId: string): void {
        etudeEcoDimensionnementId.value = analyseId
        etudeEcoDialogVisible.value = true
    }

    /**
     * Rafraîchit les jeux de données PV courants après création d’une étude économique.
     */
    async function refreshPvDataAfterEtudeEco(): Promise<void> {
        const bid = getBuildingId.value
        const sid = pvStore.getSourceId
        if (!bid || !sid) return
        const ids = { sourceId: sid, buildingId: bid }
        try {
            await Promise.all([
                pvStore.fetchValorisationHistoryBySource(ids),
                pvStore.fetchLastEtudeEcoForSource(ids),
                pvStore.fetchDimensionnementHistoryBySource(ids),
            ])
        } catch {
            toast.add({
                severity: "warn",
                summary: t("toastEcoRefreshError"),
                life: 8000,
            })
        }
    }

    function onEtudeEcoDialogDone(): void {
        toast.add({
            severity: "success",
            summary: t("toastEcoCreated"),
            life: 6000,
        })
        const bid = getBuildingId.value
        if (route.name === "PageBuildingPVEtudeEconomique") {
            void refreshPvDataAfterEtudeEco()
            return
        }
        if (bid) {
            void router.push({
                name: "PageBuildingPVEtudeEconomique",
                params: { _id_building: bid },
                query: route.query,
                hash: route.hash,
            })
        }
    }

    function onClickAddPdl(): void {
        sourceAppendFromState.value = SOURCES_TYPES.PDL as SourceTypeChoice
        sourceAppendDialogVisible.value = true
    }

    async function onSourceAppendDone(): Promise<void> {
        sourceAppendDialogVisible.value = false
        const bid = getBuildingId.value
        if (!bid) return
        await buildingStore.fetchBuilding(bid, { silent: true })
        await buildingStore.fetchBuildingSources({
            sourceTypes: METER_TYPES_LIST,
            silent: true,
            force: true,
        })
        const firstPdl = buildingStore.getMeters.find(
            (s) => s.type === SOURCES_TYPES.PDL
        )
        if (firstPdl?._id_source) {
            pvStore.setSourceId(firstPdl._id_source, { fetchSourcePv: false })
        }
    }

    function onSourceAppendCancel(): void {
        sourceAppendDialogVisible.value = false
    }

    return {
        getBuildingId,
        buildingStore,
        sourceAppendDialogVisible,
        sourceAppendFromState,
        sourceAppendDialogKey,
        getSourceId,
        getExtrapolationId,
        getCurrentSegment,
        dimensionnementDialogVisible,
        currentDimensionnementAnalyse,
        getCurrentDimensionnementForm,
        getCurrentDimensionnementAnalyseId,
        isSubmittingDimensionnement,
        etudeEcoDialogVisible,
        etudeEcoDimensionnementId,
        onClickCreateDimensionnement,
        onClickEditDimensionnement,
        onCloseDimensionnementDialog,
        onCreateDimensionnement,
        onUpdateDimensionnement,
        onRelaunchDimensionnementWithSegments,
        onClickModalEtudeEco,
        onEtudeEcoDialogDone,
        onClickAddPdl,
        onSourceAppendDone,
        onSourceAppendCancel,
    }
}

/**
 * Fournit le shell PV aux descendants (sous-pages sous {@link PageBuildingPVWrapper}).
 */
export function providePvPageShell(): PvPageShellApi {
    const shell = createPvPageShell()
    provide(PV_PAGE_SHELL_KEY, shell)
    return shell
}

/**
 * Shell PV : injecté sous le wrapper bâtiment PV, sinon instance locale (ex. Storybook).
 * En-tête PV : dialogue d’ajout PDL, modale d’étude économique, rafraîchissement bâtiment/sources après ajout.
 * L’historique dimensionnement est chargé par `PageBuildingPVWrapper` ; le `GET …/last`
 * est déclenché par `PageBuildingPVDimensionnement` ; chaque sous-page appelle les autres
 * `fetch…` du {@link usePvStore} selon ses besoins.
 */
export function usePvPageShell(): PvPageShellApi {
    const injected = inject(PV_PAGE_SHELL_KEY, null)
    if (injected) return injected
    return createPvPageShell()
}
